/**
 * One approval workflow: its name, what it is for, and its steps.
 *
 * Reached from the list. Each step names a ROLE rather than a person, so the
 * workflow survives staff turnover; the step's name says whose turn it is.
 */

import { useEffect, useState, type FormEvent } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../auth/useAuth';
import {
  sortSteps, useAddSteps, useReorderSteps, useApprovalSteps, useApprovalWorkflows, useCreateStep, useDeleteStep, useUpdateWorkflow,
} from './approvalsData';
import { ASSIGNABLE_ROLES, ROLE_DISPLAY_NAMES, type Role } from '../../types/roles';
import { isCallFailure } from '../../lib/callable';
import { Spinner } from '../../app/Spinner';
import { BackLink } from '../../shared/BackLink';
import { PageState } from '../../shared/PageState';
import { APPROVAL_STEPS_MAX, TITLE_MAX } from '../../types/fieldLimits';
import { WorkflowCoverage } from './WorkflowCoverage';
import { WorkflowHistory } from './WorkflowHistory';
import { StepSettings } from './StepSettings';
import { WorkflowRules } from './WorkflowRules';
import { namedPeople, runsWithPrevious, stageNumbers, stepSummary } from './stepText';
import { useMembers } from '../admin/useMembers';
import { WorkflowNameFields, type WorkflowText } from './WorkflowNameFields';
import { coverageText, coversAnything } from './coverage';
import { WORKFLOW_EXAMPLES } from './exampleChains';
import { useTaxonomy } from '../admin/useTaxonomy';
import styles from '../../shared/Page.module.css';
import own from './Approvals.module.css';

/** Roles that can plausibly hold an approval step. */
const APPROVER_ROLES: Role[] = ASSIGNABLE_ROLES.filter((r) => r === 'reviewer' || r === 'approver' || r === 'admin');
const LIST = '/admin/approval-workflows';

function errorText(e: unknown, fallback: string): string {
  if (isCallFailure(e)) return e.message;
  if (e instanceof Error) return e.message;
  return fallback;
}

export function ApprovalWorkflowDetailPage() {
  const { workflowId = '' } = useParams();
  const navigate = useNavigate();
  const { session } = useAuth();
  const workflows = useApprovalWorkflows(Boolean(session));
  const steps = useApprovalSteps(workflowId || null);
  const createStep = useCreateStep();
  const addSet = useAddSteps();
  const reorder = useReorderSteps();
  const deleteStep = useDeleteStep();
  const updateWorkflow = useUpdateWorkflow();
  const typeItems = useTaxonomy('documentType', Boolean(session));
  const departmentItems = useTaxonomy('department', Boolean(session));
  const types = (typeItems.data ?? []).filter((t) => t.isActive);
  const departments = (departmentItems.data ?? []).filter((d) => d.isActive);

  // A RETIRED WORKFLOW IS STILL A WORKFLOW. It is looked up in the whole list,
  // so its page opens and says it is retired; before this it answered "that
  // workflow is not here", and nothing in the product could bring it back.
  // `list` stays the ACTIVE ones, because that is what coverage and the
  // catch-all are about: a retired workflow is nobody's routing peer.
  const all = workflows.data?.workflows ?? [];
  const list = all.filter((w) => w.isActive !== false);
  const current = all.find((w) => w.workflowId === workflowId) ?? null;
  const retired = current?.isActive === false;
  const everythingElse = list.find((w) => w.isDefault) ?? null;

  const [words, setWords] = useState<WorkflowText>({ name: '', description: '', longDescription: '' });
  const [edited, setEdited] = useState(false);
  const [askSet, setAskSet] = useState<string | null>(null);
  const [editing, setEditing] = useState<string | null>(null);
  const members = useMembers(session?.orgId ?? null);
  const people = (members.data ?? []).filter((m) => m.status === 'active').map((m) => ({ id: m.uid, name: m.displayName || m.email })).sort((a, b) => a.name.localeCompare(b.name));
  const [stepName, setStepName] = useState('');
  const [stepRole, setStepRole] = useState<Role>('approver');
  const [touched, setTouched] = useState(false);
  const [askRetire, setAskRetire] = useState(false);
  const [said, setSaid] = useState<string | null>(null);

  // Start the name fields from what is saved, and again when it changes under us.
  const savedKey = `${current?.workflowId}|${current?.name}|${current?.description ?? ''}|${current?.longDescription ?? ''}`;
  useEffect(() => {
    setWords({ name: current?.name ?? '', description: current?.description ?? '', longDescription: current?.longDescription ?? '' });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [savedKey]);

  if (workflows.isLoading) return <Spinner label="Loading the workflow" />;
  if (!current) {
    return (
      <PageState title="That workflow is not here" action={{ label: 'All approval workflows', to: LIST }}>
        It may have been retired, or the link is out of date.
      </PageState>
    );
  }

  const ordered = sortSteps(steps.data?.steps ?? []);
  const nameMissing = touched && !stepName.trim();
  const stages = stageNumbers(ordered);
  const atMax = ordered.length >= APPROVAL_STEPS_MAX;
  // The next number comes from the list, so an unknown list means no number to give.
  const blocked = createStep.isPending || steps.isLoading || Boolean(steps.error) || atMax;
  const nextOrder = ordered.reduce((max, s) => Math.max(max, s.stepOrder), 0) + 1;
  // The count the pill shows: what the page actually loaded, falling back to
  // what the list counted, and null rather than a guess when neither is known.
  const stepTally = steps.isLoading || steps.error ? (current.stepCount ?? null) : ordered.length;
  const versionCount = current.versionCount ?? 0;
  const renamed = words.name.trim() !== current.name || words.description.trim() !== (current.description ?? '') || words.longDescription.trim() !== (current.longDescription ?? '');

  /** Swap a step with its neighbor and send the whole new order, so the server sets every number at once. */
  function move(index: number, by: -1 | 1) {
    const to = index + by;
    if (to < 0 || to >= ordered.length) return;
    const ids = ordered.map((s) => s.stepId);
    [ids[index], ids[to]] = [ids[to]!, ids[index]!];
    reorder.mutate({ workflowId, stepIds: ids });
  }

  function addStep(e: FormEvent) {
    e.preventDefault();
    setTouched(true);
    if (!stepName.trim()) return;
    // The next number comes from the list, so an unknown list means no number to give.
    if (steps.isLoading || steps.error || atMax) return;
    createStep.mutate(
      { workflowId, stepOrder: nextOrder, name: stepName.trim(), roleId: stepRole },
      { onSuccess: () => { setStepName(''); setTouched(false); } },
    );
  }

  return (
    <>
      <header className={styles.header}>
        <div className={styles.titled}>
          <p className={styles.eyebrow}>Approval workflow</p>
          <div className={styles.titleLine}>
            <BackLink to={LIST} label="Back to approval workflows" />
            <h1 className={styles.title}>{current.name}</h1>
          </div>
          <p className={styles.subtitle}>
            {current.isDefault
              ? 'Used when no more specific active workflow with steps matches the document.'
              : coversAnything(current)
                ? `In use, for ${coverageText(current, types, departments)}.`
                : 'Not in use yet: no document follows it. Say what it is for below.'}
          </p>
          {/* What this workflow is, at a glance. Every pill is read off the
              record: nothing here is a constant dressed up as a status. */}
          <div className={own.headPills}>
            <span className={`${own.pill} ${current.isActive === false ? own.pillOff : own.pillOn}`}>
              {current.isActive === false ? 'Retired' : 'Active'}
            </span>
            {current.isDefault && <span className={`${own.pill} ${own.pillOn}`}>Catch-all</span>}
            {stepTally !== null && (
              <span className={`${own.pill} ${stepTally === 0 ? own.pillWarn : own.pillOff}`}>
                {stepTally === 0 ? 'No steps' : stepTally === 1 ? '1 step' : `${stepTally} steps`}
              </span>
            )}
            {current.authorsMayChoose === false && <span className={`${own.pill} ${own.pillOff}`}>Not offered to authors</span>}
            {versionCount > 0 && <span className={`${own.pill} ${own.pillOff}`}>Version {versionCount}</span>}
          </div>
        </div>
      </header>

      <div className={`${styles.body} ${own.detailBody}`}>
        {updateWorkflow.error && (
          <div className={`${styles.notice} ${styles.noticeDanger}`} role="alert">{errorText(updateWorkflow.error, 'That change failed.')}</div>
        )}
        {said && <div className={`${styles.notice} ${styles.noticeInfo}`} role="status">{said}</div>}

        {retired && (
          <div className={`${styles.notice} ${styles.noticeWarn}`} role="status">
            This workflow is retired. No new document routes to it and no author can choose it, and anything
            already in approval on it finishes on it. You may still edit its steps, what it is for and its
            settings while it is retired; the changes apply if it is brought back, below. Because it routes
            nothing, its coverage may overlap a workflow in use for now, and that overlap is checked when you
            bring it back.
          </div>
        )}

        <section className={styles.section} aria-labelledby="name-title">
          <h2 className={styles.sectionTitle} id="name-title">What it is called</h2>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              if (!words.name.trim() || !renamed) return;
              updateWorkflow.mutate(
                { workflowId, name: words.name.trim(), description: words.description.trim(), longDescription: words.longDescription.trim() },
                { onSuccess: () => setSaid('Saved.') },
              );
            }}
          >
            <WorkflowNameFields idPrefix="wf" value={words} onChange={(next) => { setEdited(true); setWords(next); }} disabled={updateWorkflow.isPending} nameMissing={edited && !words.name.trim()} />
            <div className={styles.actions} style={{ marginTop: 'var(--sp-3)' }}>
              <button type="submit" className={styles.btn} disabled={!renamed || !words.name.trim() || updateWorkflow.isPending}>Save name and descriptions</button>
            </div>
          </form>
        </section>

        <WorkflowCoverage workflow={current} all={list} types={types} departments={departments} onSaved={setSaid} />

        <section className={styles.section}>
          <h2 className={styles.sectionTitle}>Steps, in order</h2>

          {(createStep.error || deleteStep.error || addSet.error || reorder.error) && (
            <div className={`${styles.notice} ${styles.noticeDanger}`} role="alert">{errorText(createStep.error ?? deleteStep.error ?? addSet.error ?? reorder.error, 'That change failed.')}</div>
          )}

          {steps.isLoading ? (
            <Spinner label="Loading steps" />
          ) : steps.error ? (
            // Never "no steps" here: the list failed, so what the workflow holds is unknown.
            <div className={`${styles.notice} ${styles.noticeDanger}`} role="alert">
              <p>The steps could not be loaded, so this page cannot say what the workflow holds. Anything you added is kept. {errorText(steps.error, '')}</p>
              <button type="button" className={styles.btn} onClick={() => void steps.refetch()}>Try again</button>
            </div>
          ) : ordered.length === 0 ? (
            <div className={`${styles.notice} ${styles.noticeWarn}`} role="status">
              This workflow has no steps, so nothing submitted to it can be approved, and documents it covers skip it. Add at least one below.
            </div>
          ) : (
            <ol className={own.steps} aria-label="This workflow's steps">
              {ordered.map((step, i) => (
                <li key={step.stepId} className={own.stepItem}>
                <div className={own.step}>
                  <span className={own.stepOrder} aria-hidden="true">{stages[i]}</span>
                  <div className={own.stepBody}>
                    <div className={own.stepName}><span className="vh">Step {stages[i]}: </span>{step.name}</div>
                    {/* The names are the title, so a long committee does not
                        push the row's buttons off a narrow screen, and nothing
                        is lost: they are spelled out again under Edit. */}
                    <div className={own.stepMeta} title={namedPeople(step, people).join(', ') || undefined}>
                      {stepSummary(step, people).join(' · ')}
                    </div>
                    {runsWithPrevious(step, i) && (
                      <div className={own.badges}><span className={own.badgeStage}>Runs with previous step</span></div>
                    )}
                  </div>
                  <div className={own.stepActions}>
                    <button type="button" className={own.iconBtn} disabled={i === 0 || reorder.isPending || deleteStep.isPending}
                      aria-label={`Move step ${i + 1}, ${step.name}, up`} title="Move up" onClick={() => move(i, -1)}>
                      <svg viewBox="0 0 20 20" width="16" height="16" aria-hidden="true"><path d="M10 15V5M5 10l5-5 5 5" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" /></svg>
                    </button>
                    <button type="button" className={own.iconBtn} disabled={i === ordered.length - 1 || reorder.isPending || deleteStep.isPending}
                      aria-label={`Move step ${i + 1}, ${step.name}, down`} title="Move down" onClick={() => move(i, 1)}>
                      <svg viewBox="0 0 20 20" width="16" height="16" aria-hidden="true"><path d="M10 5v10M5 10l5 5 5-5" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" /></svg>
                    </button>
                    <button type="button" className={styles.btn} aria-expanded={editing === step.stepId} aria-label={`Edit step ${i + 1}, ${step.name}`} onClick={() => setEditing(editing === step.stepId ? null : step.stepId)}>Edit</button>
                    <button type="button" className={styles.btn} disabled={deleteStep.isPending || reorder.isPending} aria-label={`Remove step ${i + 1}, ${step.name}`} onClick={() => deleteStep.mutate({ stepId: step.stepId })}>Remove</button>
                  </div>
                </div>
                {editing === step.stepId && (
                  <StepSettings step={step} hasStepAbove={i > 0} people={people} onDone={(m) => { setEditing(null); if (m) setSaid(m); }} />
                )}
                </li>
              ))}
            </ol>
          )}

          <form onSubmit={addStep}>
            <h3 className={own.label} style={{ fontSize: 'var(--fs-md)' }}>Add a step</h3>
            <p className={`${own.itemMeta} ${own.explain}`}>
              Give the step a name and choose the role that normally decides it. After adding it, use Edit to
              choose named people, limit it to the document&rsquo;s department, require several approvals, make it a
              committee&rsquo;s decision, run it beside the previous step, or set a deadline.
            </p>
            <div className={own.addRow}>
              <div className={own.addField}>
                <label className={own.label} htmlFor="step-name">What this step is called</label>
                <input
                  id="step-name" maxLength={TITLE_MAX} className={own.input} value={stepName}
                  aria-invalid={Boolean(nameMissing)} aria-describedby={nameMissing ? 'step-name-error' : undefined} disabled={createStep.isPending}
                  onChange={(e) => setStepName(e.target.value)}
                />
                {nameMissing && <p className={own.error} id="step-name-error">Name the step, so an approver knows what they are deciding.</p>}
              </div>
              <div className={own.addField}>
                <label className={own.label} htmlFor="step-role">Who decides it</label>
                <select id="step-role" className={own.select} value={stepRole} onChange={(e) => setStepRole(e.target.value as Role)} disabled={createStep.isPending}>
                  {APPROVER_ROLES.map((r) => <option key={r} value={r}>{ROLE_DISPLAY_NAMES[r]}</option>)}
                </select>
              </div>
              <button type="submit" className={`${styles.btn} ${styles.btnPrimary}`} disabled={createStep.isPending || steps.isLoading || Boolean(steps.error) || atMax}>
                {createStep.isPending ? 'Adding…' : 'Add step'}
              </button>
            </div>
            {atMax && <p className={own.itemMeta}>A workflow holds at most {APPROVAL_STEPS_MAX} steps. Remove one to add another.</p>}
          </form>

          <div className={own.pickStep} style={{ marginTop: 'var(--sp-5)', marginBottom: 0, borderBottom: 0, paddingBottom: 0 }}>
            <h3 className={own.label} style={{ fontSize: 'var(--fs-md)' }}>Or use a ready-made set of steps</h3>
            <p className={`${own.itemMeta} ${own.explain}`}>
              A complete chain, added in one go{ordered.length > 0 ? ' after the steps already here' : ''}. They are ordinary steps afterward: rename, remove or add to them as you like.
            </p>
            <p className={own.note}>
              These are starting points, not requirements. Adjust them to match your organization&rsquo;s own
              governance.
            </p>
            <p className={own.note}>
              Adding a set changes the steps in this workflow only. It does not change which document types or
              departments use it.
            </p>
            <ul className={own.sets} aria-label="Ready-made sets of steps">
              {WORKFLOW_EXAMPLES.map((ex) => {
                const fits = ordered.length + ex.steps.length <= APPROVAL_STEPS_MAX;
                return (
                  <li key={ex.key} className={own.set}>
                    <div className={own.setHead}>
                      <span className={own.setName}>{ex.name}</span>
                      <span className={own.itemMeta}>{ex.suits}</span>
                    </div>
                    <ol className={own.setSteps}>
                      {ex.steps.map((st, i) => (
                        <li key={st.name}><span className="vh">Step {i + 1}: </span>{st.name}<span className={own.exampleRole}> · {stepSummary(st, []).join(' · ')}</span></li>
                      ))}
                    </ol>
                    {askSet === ex.key ? (
                      <div className={styles.actions}>
                        <button type="button" className={`${styles.btn} ${styles.btnPrimary}`} disabled={blocked || addSet.isPending}
                          onClick={() => addSet.mutate({ workflowId, setKey: ex.key, steps: ex.steps }, { onSuccess: () => { setAskSet(null); setSaid(`Added the ${ex.steps.length} ${ex.steps.length === 1 ? 'step' : 'steps'} of "${ex.name}".`); } })}>
                          {addSet.isPending ? 'Adding…' : `Add ${ex.steps.length === 1 ? 'this step' : `these ${ex.steps.length} steps`}`}
                        </button>
                        <button type="button" className={styles.btn} onClick={() => setAskSet(null)}>Cancel</button>
                      </div>
                    ) : (
                      <button type="button" className={styles.btn} disabled={blocked || addSet.isPending || !fits}
                        aria-label={`Use the steps of "${ex.name}"`}
                        title={fits ? undefined : `A workflow holds at most ${APPROVAL_STEPS_MAX} steps, and these will not fit.`}
                        onClick={() => (ordered.length === 0 ? addSet.mutate({ workflowId, setKey: ex.key, steps: ex.steps }, { onSuccess: () => setSaid(`Added the ${ex.steps.length} ${ex.steps.length === 1 ? 'step' : 'steps'} of "${ex.name}".`) }) : setAskSet(ex.key))}>
                        Use these steps
                      </button>
                    )}
                  </li>
                );
              })}
            </ul>
          </div>
        </section>

        <WorkflowRules workflow={current} onSaved={setSaid} />

        <section className={styles.section} aria-labelledby="choose-title">
          <h2 className={styles.sectionTitle} id="choose-title">Choosing it by hand</h2>
          <label className={own.choice}>
            <input
              type="checkbox" id="authors-may-choose" checked={current.authorsMayChoose !== false} disabled={updateWorkflow.isPending}
              onChange={(e) => updateWorkflow.mutate({ workflowId, authorsMayChoose: e.target.checked }, { onSuccess: () => setSaid(e.target.checked ? 'Authors may now choose this workflow, when their document would otherwise use the catch-all.' : 'Authors can no longer choose this workflow. Documents still reach it by routing.') })}
            />
            <span>Authors may choose this workflow when their document would otherwise use the catch-all</span>
          </label>
          <p className={`${own.itemMeta} ${own.explain}`}>
            {current.isDefault
              ? 'This is the catch-all, so a document reaching it is one nothing else covers. Its author may send it to another workflow you have made available instead. A document with a document-type or department route of its own cannot be replaced by an author.'
              : 'A document that routes to a specific workflow by its document type or department keeps it, whatever this is set to. Administrators may choose this workflow whenever they override routing.'}
          </p>
          <p className={`${own.itemMeta} ${own.explain}`}>
            Whoever chooses must explain why, and the choice, the reason and the routed workflow are all recorded
            with the approval for the approvers to see. Turn this off for a chain that should only ever be reached
            by routing. Administrators may always choose any usable workflow.
          </p>
        </section>

        <WorkflowHistory workflowId={workflowId} versionCount={versionCount} types={types} departments={departments} />

        {/* Was "Other changes", which hid the only way to retire a workflow
            behind a heading that named nothing. The catch-all gets the section
            too, so its page says WHY neither action is offered instead of
            leaving a blank where an administrator looked for them. */}
        <section className={styles.section} aria-labelledby="status-title">
          <h2 className={styles.sectionTitle} id="status-title">Status and retirement</h2>

          {retired ? (
            <div className={own.inUse}>
              <p className={own.inUseText}>
                Bring it back to put it in use again, with whatever steps and coverage it holds now. If a
                workflow in use covers the same documents at the same level, this is refused and says which one,
                because two workflows in use cannot cover the same documents.
              </p>
              <button type="button" className={`${styles.btn} ${styles.btnPrimary}`} disabled={updateWorkflow.isPending}
                onClick={() => updateWorkflow.mutate({ workflowId, isActive: true }, { onSuccess: () => setSaid(`"${current.name}" is in use again. Documents submitted from now on can be routed to it; anything already in approval is unchanged.`) })}>
                {updateWorkflow.isPending ? 'Bringing it back…' : 'Bring it back'}
              </button>
            </div>
          ) : current.isDefault ? (
            <p className={`${own.itemMeta} ${own.explain}`}>
              This is the catch-all workflow, so it cannot be retired: every document that no other workflow
              covers follows it, and retiring it would leave those documents with a stand-in nobody chose. Make
              another workflow the catch-all before retiring this one. A workflow needs at least one step to take
              the place.
            </p>
          ) : askRetire ? (
            <div className={own.inUse}>
              <p className={own.inUseText}>
                Retire &ldquo;{current.name}&rdquo;? It leaves the list. A document already moving through it finishes on it.
                {coversAnything(current) ? ` The documents it covers will follow ${everythingElse ? `"${everythingElse.name}"` : 'the workflow for everything else'} instead.` : ''}
              </p>
              <button type="button" className={styles.btn} disabled={updateWorkflow.isPending}
                onClick={() => updateWorkflow.mutate({ workflowId, isActive: false }, { onSuccess: () => navigate(LIST) })}>
                {updateWorkflow.isPending ? 'Retiring…' : 'Retire it'}
              </button>
              <button type="button" className={styles.btn} onClick={() => setAskRetire(false)}>Keep it</button>
            </div>
          ) : (
            <>
              <div className={own.inUse}>
                <p className={own.inUseText}>
                  Use it for everything else to make it the workflow that takes whatever no other workflow covers
                  {everythingElse ? `, in place of "${everythingElse.name}"` : ''}. It will stop naming document types and departments.
                </p>
                <button
                  type="button" className={styles.btn}
                  disabled={updateWorkflow.isPending || ordered.length === 0 || Boolean(steps.error) || steps.isLoading}
                  title={ordered.length === 0 ? 'Add a step first: a workflow with no steps cannot approve anything.' : undefined}
                  onClick={() => updateWorkflow.mutate({ workflowId, isDefault: true }, { onSuccess: () => setSaid(`"${current.name}" now takes everything no other workflow covers. Any document already in approval finishes on the chain it started with.`) })}
                >
                  Use for everything else
                </button>
              </div>
              <div className={own.inUse}>
                <p className={own.inUseText}>
                  Retire it to take it out of use. Documents already in approval on it finish on it, and its history is kept.
                </p>
                <button type="button" className={styles.btn} onClick={() => setAskRetire(true)}>Retire</button>
              </div>
            </>
          )}
        </section>
      </div>
    </>
  );
}
